import {AfterViewInit, ApplicationRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {WRTCProviderService} from '@app/modules/wrtc/services/providers/wrtc-provider';
import {AccessType, ConnectionStatus, LogLevel, LogResource} from '@app/modules/shared/data/constants';
import {Store} from '@ngxs/store';
import {AppService} from '@app/services/app.service';
import {Subject} from 'rxjs';
import {ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {CommandService} from '@app/services/command.service';
import {GameModeModel, GameModeState} from '@app/state/game-mode.state';
import {LogService} from '@app/services/log.service';
import {takeUntil} from 'rxjs/operators';
import {
  MuteSelfAudioConfigurationComponent
} from '@app/modules/shared/components/dialogs/mute-self-audio-configuration/mute-self-audio-configuration.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {LocalUser, LocalUserAction, LocalUserState} from '@app/state/local-user.state';
import {WrtcService} from '@app/modules/wrtc/services/wrtc.service';
import {Whiteboard, WhiteboardState} from '@app/state/whiteboard.state';
import {CoreService} from '@app/services/core.service';
import {RemoteParticipantInfo} from '@app/models/remote-participant-info';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {UsersListService} from '@app/modules/shared/services/users-list.service';

@Component({
  selector: 'app-students-section',
  templateUrl: './students-section.component.html'
})
export class StudentsSectionComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('mediaWrapper') set wrapper(wrapper: ElementRef<HTMLElement>) {
    if (wrapper) {
      this.wrtcProviderService.setCurrentUserMediaWrapper(wrapper.nativeElement);
    }
  }

  private destroy: Subject<boolean> = new Subject<boolean>();
  public localUser: LocalUser;
  public whiteboard: Whiteboard;

  public gameMode: GameModeModel;

  public currentParticipantVoiceMeterValue = 0;
  public room: ClassroomStateModel;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public RoomConnectionState = ConnectionStatus;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public AccessType = AccessType;

  constructor(
    private appService: AppService,
    private store: Store,
    private wrtcProviderService: WRTCProviderService,
    private commandService: CommandService,
    private wrtcService: WrtcService,
    private dialogService: NgbModal,
    private coreService: CoreService,
    protected usersListService: UsersListService
  ) {
    this.listenStoreChanges();
  }

  public ngOnInit(): void {}

  public ngAfterViewInit(): void {
    if (this.wrapper) {
      this.wrtcProviderService.setCurrentUserMediaWrapper(this.wrapper.nativeElement);
    }
  }

  public ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.unsubscribe();
  }

  public listenStoreChanges(): void {
    this.store.select(ClassroomState).pipe(takeUntil(this.destroy)).subscribe(room => {
      this.room = room;
    });

    this.store.select(GameModeState).pipe(takeUntil(this.destroy)).subscribe(gameMode => {
      this.gameMode = gameMode;
    });

    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });

    this.store.select(WhiteboardState).subscribe((whiteboard: Whiteboard) => {
      this.whiteboard = whiteboard;
    });

    this.store.select(LocalUserState.getIsCurrentUserAudioProvided).pipe(takeUntil(this.destroy)).subscribe(isAudioProvided => {
      if (isAudioProvided && this.localUser.accessType === AccessType.STUDENT) {
        LogService.writeLog(`Start current student voice meter`, LogLevel.DEBUG, LogResource.WRTC);
        this.wrtcProviderService.getStudentIndividualEventEmitters(this.localUser.id).voiceMeterLevelChanged
          .pipe(takeUntil(this.destroy))
          .subscribe((level) => {
            this.currentParticipantVoiceMeterValue = this.generateStudentVoiceMeterCoefficient(level);
          });
      }
    });
  }

  /**
   * @param audioLevel value between 0 and 1
   * */
  public generateStudentVoiceMeterCoefficient(audioLevel: number): number {
    if (audioLevel >= 0.5) {
      return 5;
    } else {
      return Math.floor(audioLevel * 10);
    }
  }

  public toggleMuteSelfAudio(): void {
    if (this.localUser.audio.isProvided) {
      if (!this.localUser.audio.isMuted) {
        if (this.localUser.audio.selfMuteAttemptsCount < 2) {
          this.openMuteSelfAudioConfirmationDialog().then(() => {
            this.wrtcService.sendSelfAudioMute().subscribe();
          }, (reason) => {
            console.log('dialog closed ' + reason);
          });
        }
      } else {
        if (!this.room.isClassroomMuted) {
          this.wrtcProviderService.toggleDisableSelfAudio(false);
          this.store.dispatch(new LocalUserAction.SetIsAudioMuteState(false));
          this.wrtcService.sendAudioUnmute(this.localUser.id).subscribe();
        }
      }
    }
  }

  public openMuteSelfAudioConfirmationDialog(): Promise<any> {
    const dialog: NgbModalRef = this.dialogService.open(MuteSelfAudioConfigurationComponent);

    dialog.componentInstance.message = this.localUser.audio.selfMuteAttemptsCount > 0 ?
      'This is your last opportunity to mute yourself.  <br> Do you want to continue?' : 'Muting yourself is only possible 2 times per lesson. <br> Do you want to continue?';

    return dialog.result;
  }

}
